type DeeplinkPreflightHandler = (episodeId: string, seriesId?: string, seasonId?: string) => void;

const MAX_WAIT_TIME = 5000;
const CHECK_INTERVAL = 50;

interface PendingDeeplinkData {
  episodeId: string;
  seriesId?: string;
  seasonId?: string;
}

class DeeplinkPreflightEvents {
  private handler: DeeplinkPreflightHandler | null = null;
  private processing = false;
  private pendingEpisodeId: string | null = null;
  private pendingDeeplinkData: PendingDeeplinkData | null = null;

  setHandler(handler: DeeplinkPreflightHandler) {
    console.log('[DEEPLINK-EVENTS] Handler registered');
    this.handler = handler;

    if (this.pendingDeeplinkData && this.processing) {
      console.log('[DEEPLINK-EVENTS] Replaying pending deeplink after handler re-registration');
      const { episodeId, seriesId, seasonId } = this.pendingDeeplinkData;
      setTimeout(() => {
        if (this.handler) {
          console.log(`[DEEPLINK-EVENTS] Executing replayed deeplink: episodeId=${episodeId}`);
          this.handler(episodeId, seriesId, seasonId);
        }
      }, 50);
    }
  }

  clearHandler() {
    if (this.processing) {
      console.log('[DEEPLINK-EVENTS] Handler clear BLOCKED - deeplink still processing');
      return;
    }
    console.log('[DEEPLINK-EVENTS] Handler cleared');
    this.handler = null;
  }

  safeClearHandler() {
    if (this.processing) {
      console.log('[DEEPLINK-EVENTS] safeClearHandler: BLOCKED - deeplink still processing');
      return false;
    }
    console.log('[DEEPLINK-EVENTS] safeClearHandler: Handler cleared');
    this.handler = null;
    this.pendingDeeplinkData = null;
    return true;
  }

  forceClearHandler() {
    console.log('[DEEPLINK-EVENTS] forceClearHandler: Handler force-cleared');
    this.handler = null;
    this.processing = false;
    this.pendingEpisodeId = null;
    this.pendingDeeplinkData = null;
  }

  setProcessing(isProcessing: boolean, episodeId?: string) {
    this.processing = isProcessing;
    this.pendingEpisodeId = isProcessing && episodeId ? episodeId : null;

    if (!isProcessing) {
      this.pendingDeeplinkData = null;
    }

    console.log(`[DEEPLINK-EVENTS] Processing: ${isProcessing}, episodeId: ${episodeId || 'none'}`);
  }

  isProcessing(): boolean {
    return this.processing;
  }

  getPendingEpisodeId(): string | null {
    return this.pendingEpisodeId;
  }

  getPendingDeeplinkData(): PendingDeeplinkData | null {
    return this.pendingDeeplinkData;
  }

  hasPendingDeeplink(): boolean {
    return this.pendingDeeplinkData !== null && this.processing;
  }

  emit(episodeId: string, seriesId?: string, seasonId?: string): boolean {
    if (this.handler) {
      console.log(`[DEEPLINK-EVENTS] Emitting to handler: episodeId=${episodeId}`);
      this.handler(episodeId, seriesId, seasonId);
      return true;
    }
    console.log('[DEEPLINK-EVENTS] No handler available, emit failed');
    return false;
  }

  hasHandler(): boolean {
    return this.handler !== null;
  }

  async waitForHandler(timeoutMs: number = MAX_WAIT_TIME): Promise<boolean> {
    if (this.handler) {
      console.log('[DEEPLINK-EVENTS] Handler already available');
      return true;
    }

    console.log(`[DEEPLINK-EVENTS] Waiting for handler (timeout: ${timeoutMs}ms)...`);

    return new Promise((resolve) => {
      const startTime = Date.now();

      const checkHandler = () => {
        if (this.handler) {
          console.log('[DEEPLINK-EVENTS] Handler became available');
          resolve(true);
          return;
        }

        if (Date.now() - startTime >= timeoutMs) {
          console.log('[DEEPLINK-EVENTS] Timeout waiting for handler');
          resolve(false);
          return;
        }

        setTimeout(checkHandler, CHECK_INTERVAL);
      };

      checkHandler();
    });
  }

  async emitWithWait(
    episodeId: string,
    seriesId?: string,
    seasonId?: string,
    timeoutMs: number = MAX_WAIT_TIME
  ): Promise<boolean> {
    this.pendingDeeplinkData = { episodeId, seriesId, seasonId };
    this.setProcessing(true, episodeId);

    const handlerAvailable = await this.waitForHandler(timeoutMs);

    if (handlerAvailable && this.handler) {
      console.log(`[DEEPLINK-EVENTS] Emitting after wait: episodeId=${episodeId}`);
      this.handler(episodeId, seriesId, seasonId);
      return true;
    }

    console.log('[DEEPLINK-EVENTS] Handler not available after wait, clearing processing flag');
    this.setProcessing(false);
    return false;
  }
}

export const deeplinkPreflightEvents = new DeeplinkPreflightEvents();
